<template>
  <v-row class="section-container">
    <v-col cols="12" class="header-with-select-box">
      <v-row align="center" justify="space-between">
        <v-col cols="6">
          <h3 class="text-primary weight-600">Submission Fax Form</h3>
        </v-col>
        <v-col v-if="totalPages > 1" cols="6" class="text-end pdf-pager">
          <v-btn v-if="currentPage > 1" @click="prevPage">←</v-btn>
          <v-btn v-if="currentPage < totalPages" class="ml-5" @click="nextPage">→</v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" class="d-flex justify-center">
          <div ref="fitContainer" class="pdf-fit" :style="fitHeight != null ? { height: fitHeight + 'px' } : {}">
            <div
              class="position-relative pdf-fit__inner"
              :style="{ transform: `scale(${fitScale})`, transformOrigin: 'top center' }">
              <canvas ref="pdfCanvas" width="100%" class="d-block v-btn--readonly"></canvas>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import * as pdfjs from "pdfjs-dist";
pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.mjs", import.meta.url).toString();

const props = defineProps({
  pdfForm: {
    type: Blob,
    required: true,
  },
});

const pdfCanvas = ref(null);
const scale = 96 / 72;
const currentPage = ref(1);
const totalPages = ref(1);
let pdfDoc = null;

// Scale the native-width PDF canvas down to its container so it never overflows on mobile.
const fitContainer = ref(null);
const fitScale = ref(1);
const fitHeight = ref(null);
let resizeObserver = null;

function computeFit() {
  const canvas = pdfCanvas.value;
  const container = fitContainer.value;
  if (!canvas || !container || !canvas.width || !container.clientWidth) return;
  const s = Math.min(1, container.clientWidth / canvas.width);
  const h = canvas.height * s;
  if (Math.abs(s - fitScale.value) < 0.0005 && fitHeight.value != null && Math.abs(h - fitHeight.value) < 0.5) return;
  fitScale.value = s;
  fitHeight.value = h;
}

onMounted(async () => {
  const arrayBuffer = await props.pdfForm.arrayBuffer();
  const loadingTask = pdfjs.getDocument({ data: arrayBuffer });
  pdfDoc = await loadingTask.promise;
  totalPages.value = pdfDoc.numPages;

  await renderPage(currentPage.value);

  if (fitContainer.value) {
    resizeObserver = new ResizeObserver(() => computeFit());
    resizeObserver.observe(fitContainer.value);
  }
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
});

async function renderPage(pageNumber) {
  if (!pdfDoc) return;

  const page = await pdfDoc.getPage(pageNumber);
  const viewport = page.getViewport({ scale: scale });

  const canvas = pdfCanvas.value;
  if (!canvas) {
    return;
  }
  const context = canvas.getContext("2d");
  canvas.height = viewport.height;
  canvas.width = viewport.width;

  const renderTask = page.render({
    canvasContext: context,
    viewport: viewport,
  });
  await renderTask.promise;
  computeFit();
}

async function nextPage() {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
    await renderPage(currentPage.value);
  }
}

async function prevPage() {
  if (currentPage.value > 1) {
    currentPage.value--;
    await renderPage(currentPage.value);
  }
}
</script>

<style scoped>
.pdf-fit {
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  display: flex;
  justify-content: center;
}

.pdf-fit__inner {
  display: inline-block;
  vertical-align: top;
}
</style>
